import { useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { useIsNarrow } from '@/components/blog/useIsNarrow';
import { useIsDark } from '../../useIsDark';

/**
 * MeetingsPerMeetingCostChart — blog16, Figure 2: the two numbers that decide it, on one canvas.
 *
 * One visualisation, two measures, one shared category axis — because the question a founder actually
 * asks is "how many investor meetings, at what cost each", and those two numbers only mean anything
 * side by side. Bars (left axis) = estimated investor meetings per month; line (right axis) = estimated
 * cost per investor meeting. Read left to right: MORE meetings and LOWER bars-per-meeting is better, so
 * the shape to look for is bars rising while the line falls.
 *
 * Two y-axes rather than two charts: a founder comparing plans is making ONE trade-off (volume against
 * unit cost), and splitting it across two pictures is how a comparison becomes incomparable.
 *
 * Currency: USD, and it is the PUBLISHED currency for every plan on this page (PO 2026-09-20). There
 * is therefore no conversion anywhere in this file and no exchange-rate assumption for a reader to
 * accept on trust — the same reason credits are pegged to USD.
 *
 * Colour is never the only identity (Rule 21): every bar carries its own category label, and the two
 * series are named in the legend and the tooltip.
 * CETERIS PARIBUS (PO 2026-09-20). The reply rate belongs to the SENDING METHOD, not to the vendor, so
 * the SAME rate is applied to every column — ours included. An earlier version gave our own columns 6.5%
 * and the database tools 1.5% inside one chart, which decided the verdict with an input we picked, and
 * priced a competitor's list as though the buyer were obliged to send it badly. The rate is now a
 * scenario the reader switches, and switching it moves EVERY column together, so no ranking here can be
 * produced by handing one vendor a better rate.
 *
 * The estimates, from the same published bands as the tables:
 *   replies  = the method's reply rate x investors contacted  (applied to every vendor alike)
 *   meetings = 40% of replies                                 (midpoint of the 30-50% band)
 *   cost per meeting = plan price / estimated meetings
 *
 * A price may also cover less than the whole job. Angel Match and OpenVC DO send — Angel's own pricing
 * page caps "investors you can email per month", OpenVC's allows 5 sends a day — but both send from the
 * customer's OWN domain, so the mailbox, the domain warm-up and the copy stay with the buyer. Raisy
 * carries those. Their figure is therefore a FLOOR: marked "† you bring the mailbox", never hidden in a
 * footnote.
 *
 * Every figure below is an estimate. Nothing here is charged per meeting - we charge per reachout (MR20).
 */

const BRAND = {
  teal: '#0AB3BF',
  slate: '#94A3B8',
  amber: '#F59E0B',
};

/** The rate belongs to the METHOD the buyer sends with, so it is applied to every column alike. */
const METHODS = {
  tailored: {
    label: 'Tailored sending',
    replyPct: 6.5,
    note: 'a fit-ranked list and an email written for each investor — the midpoint of the 3–10% band published for cold investor outreach',
  },
  generic: {
    label: 'Generic sending',
    replyPct: 1.5,
    note: 'a template or a bulk send — the published floor for generic cold email',
  },
} as const;
type MethodKey = keyof typeof METHODS;

/** Midpoint of the 30–50% reply-to-meeting band. */
const MEETING_RATE = 0.4;

type Row = {
  plan: string;
  /** Two lines, always: family on the first, tier on the second. One long line per category collides
   *  with its neighbours once there are seven of them, which is what this replaced. */
  label: string;
  /** Published monthly price — USD for every plan, so nothing needs converting. */
  pricePerMonth: number;
  /** Investors EMAILED per month — the cap each vendor actually sells. Angel Match's own page caps
   *  "investors you can email per month" (100 / 500 / 2,000) and OpenVC allows 5 sends a day; Raisy
   *  caps reachouts the same way. It is a count of emails sent, not of rows unlocked. */
  contactsPerMonth: number;
  /** True when the buyer must bring their own mailbox and warmed domain — Angel Match and OpenVC send
   *  from YOUR domain, so their price excludes infrastructure Raisy carries. Marked † on the axis. */
  ownInfra?: boolean;
};

const ROWS: Row[] = [
  { plan: 'Raisy Starter', label: 'Raisy\nStarter', pricePerMonth: 49, contactsPerMonth: 250 },
  { plan: 'Raisy Growth', label: 'Raisy\nGrowth', pricePerMonth: 129, contactsPerMonth: 750 },
  { plan: 'Raisy Scale', label: 'Raisy\nScale', pricePerMonth: 279, contactsPerMonth: 1700 },
  { plan: 'Angel Match Basic', label: 'Angel Match\nBasic', pricePerMonth: 59, contactsPerMonth: 100, ownInfra: true },
  { plan: 'Angel Match Growth', label: 'Angel Match\nGrowth', pricePerMonth: 99, contactsPerMonth: 500, ownInfra: true },
  { plan: 'Angel Match Premium', label: 'Angel Match\nPremium', pricePerMonth: 349, contactsPerMonth: 2000, ownInfra: true },
  { plan: 'OpenVC Premium', label: 'OpenVC\nPremium', pricePerMonth: 99, contactsPerMonth: 150, ownInfra: true },
];

const isRaisy = (r: Row) => r.plan.startsWith('Raisy');
const priceLabel = (r: Row) => `$${r.pricePerMonth}`;
const axisLabel = (r: Row) => (r.ownInfra ? `${r.label} †` : r.label);

/** Derived, never typed twice: both scenarios and both views read from this one pair of numbers, so a
 *  figure in the chart cannot drift from the caption that explains it. */
const meetingsFor = (r: Row, m: MethodKey) =>
  Math.round(r.contactsPerMonth * (METHODS[m].replyPct / 100) * MEETING_RATE * 10) / 10;
const costPerMeetingFor = (r: Row, m: MethodKey) => Math.round(r.pricePerMonth / meetingsFor(r, m));

/**
 * VALUE FOR MONEY: estimated investor meetings per $100 spent. Derived from the two figures already in
 * the table (100 / cost per meeting), so it cannot drift from them — and it is the shortest honest
 * answer to "which one is worth it". Never charged; we charge per reachout.
 */
const meetingsPer100Usd = (r: Row, m: MethodKey) => Math.round((100 / costPerMeetingFor(r, m)) * 10) / 10;

type Mode = 'value' | 'details';

export const MeetingsPerMeetingCostChart = ({
  height,
  dark,
}: {
  height?: number;
  dark?: boolean;
}) => {
  const narrow = useIsNarrow();
  const liveDark = useIsDark();
  const isDark = dark ?? liveDark;
  const [mode, setMode] = useState<Mode>('value');
  const [method, setMethod] = useState<MethodKey>('tailored');

  const C = isDark
    ? { bg: '#0F172A', axis: '#E2E8F0', muted: '#94A3B8', grid: '#334155', tabBorder: '#334155' }
    : { bg: '#FFFFFF', axis: '#334155', muted: '#64748B', grid: '#CBD5E1', tabBorder: '#CBD5E1' };

  const tabStyle = (active: boolean) => ({
    backgroundColor: active ? BRAND.teal : 'transparent',
    borderColor: active ? BRAND.teal : C.tabBorder,
    color: active ? '#FFFFFF' : C.muted,
  });

  const chartHeight = height ?? (narrow ? 380 : 400);

  // ---------------------------------------------------------------- VALUE FOR MONEY (the DEFAULT)
  // One bar per plan, higher is better. It is the default because it is the question a founder is
  // actually asking, and one bar per plan is readable at a glance where two axes are not. Derived from
  // the same two figures as the table (100 / cost per meeting), so it cannot drift from them.
  const valueOption = {
    backgroundColor: C.bg,
    tooltip: {
      trigger: 'axis',
      appendToBody: true,
      backgroundColor: '#1E293B',
      borderColor: C.grid,
      textStyle: { color: '#F8FAFC', fontSize: 13 },
      formatter: (params: { dataIndex: number }[]) => {
        const r = ROWS[params[0].dataIndex];
        if (!r) return '';
        return (
          `<b>${r.plan}</b> — ${priceLabel(r)}/month<br/>` +
          `<b>${meetingsPer100Usd(r, method)}</b> est. investor meetings per $100 spent<br/>` +
          `<span style="color:#94A3B8">est. ${meetingsFor(r, method)} investor meetings/month at $${costPerMeetingFor(r, method)} each${r.ownInfra ? ' \u2014 you bring the mailbox and the copy' : ''}</span>`
        );
      },
    },
    grid: { left: narrow ? 44 : 58, right: narrow ? 16 : 24, top: narrow ? 30 : 38, bottom: narrow ? 118 : 82 },
    xAxis: {
      type: 'category',
      data: ROWS.map(axisLabel),
      axisLabel: {
        color: C.axis,
        fontSize: narrow ? 10 : 11,
        interval: 0,
        lineHeight: 13,
        rotate: narrow ? 34 : 0,
        align: narrow ? 'right' : 'center',
        verticalAlign: narrow ? 'top' : 'middle',
        margin: narrow ? 12 : 10,
      },
      axisLine: { lineStyle: { color: C.grid } },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      name: narrow ? '' : 'est. investor meetings per $100 spent',
      nameGap: 20,
      nameTextStyle: { color: C.muted, fontSize: 11, align: 'left', padding: [0, 0, 8, 0] },
      axisLabel: { color: C.muted, fontSize: 11 },
      splitLine: { lineStyle: { color: C.grid, opacity: 0.35 } },
    },
    series: [
      {
        type: 'bar',
        data: ROWS.map((r) => ({
          value: meetingsPer100Usd(r, method),
          itemStyle: { color: isRaisy(r) ? BRAND.teal : BRAND.slate, borderRadius: [4, 4, 0, 0] },
        })),
        barMaxWidth: 58,
        label: {
          show: true,
          position: 'top',
          distance: 6,
          color: C.axis,
          fontSize: 11,
          fontWeight: 'bold' as const,
          formatter: (p: { value: number }) => `${p.value}`,
        },
      },
    ],
  };

  const option = {
    backgroundColor: C.bg,
    legend: {
      bottom: 0,
      textStyle: { color: C.muted, fontSize: 11 },
      data: ['est. investor meetings / month', 'est. cost per investor meeting (USD)'],
    },
    tooltip: {
      trigger: 'axis',
      appendToBody: true,
      backgroundColor: '#1E293B',
      borderColor: C.grid,
      textStyle: { color: '#F8FAFC', fontSize: 13 },
      formatter: (params: { dataIndex: number }[]) => {
        const r = ROWS[params[0].dataIndex];
        if (!r) return '';
        return (
          `<b>${r.plan}</b><br/>` +
          `est. investor meetings / month: <b>${meetingsFor(r, method)}</b><br/>` +
          `est. cost per investor meeting: <b>$${costPerMeetingFor(r, method)}</b>` +
          (r.ownInfra ? `<br/><span style="color:#94A3B8">sends from YOUR domain \u2014 excludes the mailbox, the domain warm-up and the copy</span>` : '')
        );
      },
    },
    // The labels are the space problem, not the bars: seven categories on a phone leave ~49px each, so
    // every label is TWO lines (family over tier) and rotates on narrow screens rather than colliding.
    // TOP: the tallest bar's own value label sits above the bar, and inside a tight grid it collides
    // with the axis name — the PO caught exactly that. 38/30 gives the label its own room.
    grid: { left: narrow ? 44 : 60, right: narrow ? 48 : 64, top: narrow ? 30 : 38, bottom: narrow ? 118 : 82 },
    xAxis: {
      type: 'category',
      data: ROWS.map(axisLabel),
      axisLabel: {
        color: C.axis,
        fontSize: narrow ? 10 : 11,
        interval: 0,
        lineHeight: 13,
        rotate: narrow ? 34 : 0,
        align: narrow ? 'right' : 'center',
        verticalAlign: narrow ? 'top' : 'middle',
        margin: narrow ? 12 : 10,
      },
      axisLine: { lineStyle: { color: C.grid } },
      axisTick: { show: false },
    },
    yAxis: [
      {
        type: 'value',
        name: narrow ? '' : 'est. investor meetings / month',
        nameTextStyle: { color: C.muted, fontSize: 11, align: 'left' },
        axisLabel: { color: C.muted, fontSize: 11 },
        splitLine: { lineStyle: { color: C.grid, opacity: 0.35 } },
      },
      {
        type: 'value',
        name: narrow ? '' : 'est. cost per investor meeting (USD)',
        nameTextStyle: { color: C.muted, fontSize: 11, align: 'right' },
        axisLabel: { color: C.muted, fontSize: 11, formatter: (v: number) => `$${v}` },
        splitLine: { show: false },
      },
    ],
    series: [
      {
        name: 'est. investor meetings / month',
        type: 'bar',
        yAxisIndex: 0,
        data: ROWS.map((r) => ({
          value: meetingsFor(r, method),
          itemStyle: { color: isRaisy(r) ? BRAND.teal : BRAND.slate, borderRadius: [4, 4, 0, 0] },
        })),
        barMaxWidth: 54,
        label: {
          show: true,
          position: 'top',
          color: C.axis,
          fontSize: 11,
          formatter: (p: { value: number }) => `${p.value}`,
        },
      },
      {
        name: 'est. cost per investor meeting (USD)',
        type: 'line',
        yAxisIndex: 1,
        data: ROWS.map((r) => costPerMeetingFor(r, method)),
        smooth: false,
        symbol: 'circle',
        symbolSize: 9,
        lineStyle: { color: BRAND.amber, width: 2.5 },
        itemStyle: { color: BRAND.amber, borderColor: '#FFFFFF', borderWidth: 1 },
        label: {
          show: !narrow,
          position: 'bottom',
          color: BRAND.amber,
          fontSize: 11,
          formatter: (p: { value: number }) => `$${p.value}`,
        },
      },
    ],
  };

  return (
    <figure className="my-5">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <div className="flex gap-2" role="tablist" aria-label="Comparison view">
          {(['value', 'details'] as Mode[]).map((m) => (
            <button
              key={m}
              type="button"
              role="tab"
              aria-selected={mode === m}
              onClick={() => setMode(m)}
              style={tabStyle(mode === m)}
              className="rounded-md border px-3 py-1.5 text-xs font-medium transition"
            >
              {m === 'value' ? 'Value for money' : 'Details'}
            </button>
          ))}
        </div>
        <span className="text-xs opacity-50" aria-hidden="true">
          &middot;
        </span>
        <div className="flex gap-2" role="tablist" aria-label="Sending method">
          {(Object.keys(METHODS) as MethodKey[]).map((k) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={method === k}
              onClick={() => setMethod(k)}
              style={tabStyle(method === k)}
              className="rounded-md border px-3 py-1.5 text-xs font-medium transition"
            >
              {METHODS[k].label} ({METHODS[k].replyPct}%)
            </button>
          ))}
        </div>
      </div>
      <p className={`mb-2 text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
        The reply rate is the <strong>method&rsquo;s</strong>, not the vendor&rsquo;s, so it is applied to{' '}
        <strong>every</strong> column alike &mdash; ours included. Currently{' '}
        {METHODS[method].note}. Switching it moves every bar together, so no ranking on this chart is
        produced by giving one vendor a better rate than another.
      </p>
      <ReactECharts
        option={mode === 'value' ? valueOption : option}
        style={{ height: chartHeight, width: '100%' }}
        notMerge
      />
      <figcaption className={`mt-1 text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
        {mode === 'value' ? (
          <>
            <strong>Higher is better.</strong> Estimated investor meetings per $100 spent, at{' '}
            <strong>{METHODS[method].replyPct}% replies for every column</strong> ({METHODS[method].label.toLowerCase()})
            and 40% of replies becoming a meeting. Every plan counts the same thing &mdash; emails sent to
            unique investors per month, which is the cap each vendor sells. Rows marked <strong>†</strong>{' '}
            send from <strong>your own domain</strong>: the mailbox, the domain warm-up and the copy stay
            with you, so their figure is a <strong>floor</strong> &mdash; add your own infrastructure and
            copywriting before comparing it with a price that carries them. Every price is USD, so the
            ranking is like-for-like with nothing converted. Switch to<strong> Details</strong> for the
            numbers behind it, or switch the sending method above to see both scenarios. All estimates,
            ours included &mdash; and we charge per reachout, never per meeting.
          </>
        ) : (
          <>
            Bars: estimated investor meetings per month, at <strong>{METHODS[method].replyPct}% replies for
            every column</strong> ({METHODS[method].label.toLowerCase()}) and 40% of replies becoming a
            meeting. Line (right axis): the plan price divided by those estimated meetings. Read it as
            a trade:{' '}
            <strong>the bars should rise and the line should fall</strong>. Rows marked <strong>†</strong>{' '}
            send from your own domain, so their figure excludes the mailbox and the copy &mdash; a low
            number there is not the whole cost. All estimates, ours included. We charge per reachout,
            never per meeting.
          </>
        )}
      </figcaption>
    </figure>
  );
};
